
export interface ILatestVerificationState {
	verification_state: string;
	overrides: string[];
	last_verified_at: string;
	failures?: any;
}

export interface IBookingStateEventBase {
	created_at: string;
	id: number;
	event_id: string;
	state_event_type: unknown;
}
export interface IBookingStateEventPaymentFailed extends IBookingStateEventBase {
	state_event_type: 'PAYMENT_FAILED';
}
export interface IBookingStateEventPaymentSuccessful extends IBookingStateEventBase {
	state_event_type: 'PAYMENT_SUCCESSFUL';
}
export interface IBookingStateEventFirstBooking extends IBookingStateEventBase {
	state_event_type: 'FIRST_BOOKING';
}
export interface IBookingStateEventVerificationPass extends IBookingStateEventBase {
	state_event_type: 'VERIFICATION_PASSED';
}
export interface IBookingStateEventVerificationFailed extends IBookingStateEventBase {
	state_event_type: 'VERIFICATION_FAILED';
}
export interface IBookingStateEventDiscrepancyExceeded extends IBookingStateEventBase {
	state_event_type: 'DISCREPANCY_THRESHOLD_EXCEEDED';
	state_event_context: {
		discrepancy_rate: number;
		acceptable_discrepancy_rate: number;
	};
}
export interface IBookingStateEventDiscrepApproved extends IBookingStateEventBase {
	state_event_type: 'DISCREPANCY_APPROVED';
}
export interface IBookingStateEventZeroValue extends IBookingStateEventBase {
	state_event_type: 'ZERO_VALUE_BOOKING';
}
export interface IBookingStateEventPotentialFraud extends IBookingStateEventBase {
	state_event_type: 'POTENTIALLY_FRAUDULENT';
}
export interface IBookingStateEventConfirmedNonFraud extends IBookingStateEventBase {
	state_event_type: 'CONFIRMED_NON_FRAUDULENT';
}
export interface IBookingStateEventSnoStation extends IBookingStateEventBase {
	state_event_type: 'SNOSTATION_BOOKING';
}
export interface IBookingStateEventSnoStationApproved extends IBookingStateEventBase {
	state_event_type: 'SNOSTATION_APPROVED';
}

export interface IBookingStateEventWithoutContext extends IBookingStateEventBase {
	state_event_context: never;
}

export interface IBookingStateEventMiscellaneous extends IBookingStateEventBase {
	state_event_type: 'BOOKING_BREAKDOWN_FAILED'
	| 'EMAIL_SCHEDULE_SUCCESS'
	| 'EMAIL_SCHEDULE_FAIL'
	| 'BOOKING_MANUALLY_CONFIRMED'
	| 'PRELOAD_FAILED'
	| 'CANCELLATION_WINDOW_CLOSED'
	| 'INVALID_GUEST_DETAILS'
	| 'ACCELERATED_PAYMENT_OPTED_OUT'
	| 'AUTO_CANCELLATION_SKIPPED'
	| 'AUTO_CANCELLATION_ATTEMPTED'
	| 'AUTO_CANCELLATION_SUCCESS'
	| 'AUTO_CANCELLATION_FAILED'
	| 'INSUFFICIENT_PAYMENT_DETAILS'
	| 'FAILED_TO_RETRIEVE_GUEST_COMMS'
	| 'GUEST_COMMS_MANUALLY_ADDED';
}

export type IBookingStateEvent = IBookingStateEventPaymentFailed |
	IBookingStateEventPaymentSuccessful |
	IBookingStateEventFirstBooking |
	IBookingStateEventVerificationPass |
	IBookingStateEventVerificationFailed |
	IBookingStateEventDiscrepancyExceeded |
	IBookingStateEventDiscrepApproved |
	IBookingStateEventZeroValue |
	IBookingStateEventPotentialFraud |
	IBookingStateEventConfirmedNonFraud |
	IBookingStateEventSnoStation |
	IBookingStateEventSnoStationApproved |
	IBookingStateEventMiscellaneous;

export const generateCommentBookingStateEvent = (bookingEvent: IBookingStateEvent): string => {
	switch (bookingEvent.state_event_type) {
		case 'PAYMENT_FAILED':
			return 'Payment - failed to process.';
		case 'PAYMENT_SUCCESSFUL':
			return 'Payment - successfully processed.';
		case 'FIRST_BOOKING':
			return 'Booking marked as owners first.';
		case 'VERIFICATION_PASSED':
			return 'Passed - verification process.';
		case 'VERIFICATION_FAILED':
			return 'Failed - verifications process';
		case 'DISCREPANCY_THRESHOLD_EXCEEDED':
			return `Discrepancy - threshold exceeded. Failed value: ${(bookingEvent.state_event_context.discrepancy_rate * 100).toFixed(2)}%.
			Accepted value at time: ${bookingEvent.state_event_context.acceptable_discrepancy_rate * 100}%`;
		case 'DISCREPANCY_APPROVED':
			return 'Discrepancy - COps confirmed okay to proceed with booking.';
		case 'ZERO_VALUE_BOOKING':
			return 'TBV for booking is zero.';
		case 'POTENTIALLY_FRAUDULENT':
			return 'Potentially Fraudulent - booking requires manually check by COps.';
		case 'CONFIRMED_NON_FRAUDULENT':
			return 'Potentially Fraudulent - COps confirmed okay to proceed with booking.';
		case 'SNOSTATION_BOOKING':
			return 'SnoStation - booking requires manually check by COps.';
		case 'SNOSTATION_APPROVED':
			return 'SnoStation - COps confirmed ok to proceed with booking.';
		case 'BOOKING_BREAKDOWN_FAILED':
			return 'Failed to generate booking breakdown for booking.';
		case 'EMAIL_SCHEDULE_SUCCESS':
			return 'Emails - successfully scheduled emails.';
		case 'EMAIL_SCHEDULE_FAIL':
			return 'Email - failed schedule emails. Please send manually';
		case 'BOOKING_MANUALLY_CONFIRMED':
			return 'Booking has been manually confirmed and emails sent to host/guest';
		case 'PRELOAD_FAILED':
			return 'Failed to preload CC details for booking';
		case 'CANCELLATION_WINDOW_CLOSED':
			return 'Cancellation window for booking has closed';
		case 'INVALID_GUEST_DETAILS':
			return 'The guest\'s details are invalid (usually LastMinute.com)';
		case 'ACCELERATED_PAYMENT_OPTED_OUT':
			return 'Payment - The guest has opted-out of an accelerated payment for this booking';
		case 'AUTO_CANCELLATION_SKIPPED':
			return 'Auto cancellation escalated';
		case 'AUTO_CANCELLATION_ATTEMPTED':
			return 'Auto cancellation attempt started - waiting for payment outcome';
		case 'AUTO_CANCELLATION_SUCCESS':
			return 'Auto cancellation processed';
		case 'AUTO_CANCELLATION_FAILED':
			return 'Auto cancellation failed';
		case 'INSUFFICIENT_PAYMENT_DETAILS':
			return 'Insufficient payment details';
		case 'FAILED_TO_RETRIEVE_GUEST_COMMS':
			return 'Failed to automatically retrieve guest comms from channel';
		case 'GUEST_COMMS_MANUALLY_ADDED':
			return 'Guest comms added manually to booking notes';
		default:
			return `No description provided for state event <${((bookingEvent as IBookingStateEventBase).state_event_type)}>`;
	}
};
